/**
 * Display time-zone helpers (ADR-0160).
 *
 * The IANA zone list + DST come from the platform (`Intl`) — no dependency
 * (CLAUDE.md §8). These helpers back BOTH the dashboard picker and the
 * server-side PATCH validation for `account.display_time_zone` /
 * `site.display_time_zone`, so the set of acceptable zones is defined in
 * exactly ONE place.
 *
 * Storage / wire / signing is UNAFFECTED — timestamps stay ISO-8601 UTC
 * (spec §3, CLAUDE.md §3). This is a presentation preference only; nothing
 * here converts a stored, exported, or signed timestamp.
 */
/** The effective display zone when no preference is set anywhere. */
export const DEFAULT_TIME_ZONE = 'UTC';
/**
 * Effective display zone for a surface: the per-site override wins, else the
 * account-wide default, else UTC. `null` / `undefined` / empty string all
 * mean "not set → inherit". Pure; safe on the server and the client.
 */
export function resolveDisplayTimeZone(siteTz, accountTz) {
    if (typeof siteTz === 'string' && siteTz.length > 0)
        return siteTz;
    if (typeof accountTz === 'string' && accountTz.length > 0)
        return accountTz;
    return DEFAULT_TIME_ZONE;
}
/**
 * Common zones pinned to the top of the picker for ergonomics. Every entry
 * is also present in {@link SUPPORTED_TIME_ZONES}; this is purely display
 * ordering, not a separate allow-list.
 */
export const COMMON_TIME_ZONES = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Sao_Paulo',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Africa/Johannesburg',
    'Asia/Dubai',
    'Asia/Kolkata',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Sydney',
];
function computeSupportedZones() {
    // Seed with UTC + the pinned common zones so the set is ALWAYS a superset
    // of what the picker offers — regardless of engine. ICU versions differ:
    // some omit 'UTC', and some return an older alias (e.g. 'Asia/Calcutta'
    // for the canonical 'Asia/Kolkata'). Seeding + union keeps picker and
    // validator from ever diverging, and the module never throws at import
    // even on runtimes without `Intl.supportedValuesOf`.
    const set = new Set([DEFAULT_TIME_ZONE, ...COMMON_TIME_ZONES]);
    try {
        const intl = Intl;
        if (typeof intl.supportedValuesOf === 'function') {
            const zones = intl.supportedValuesOf('timeZone');
            if (Array.isArray(zones))
                for (const z of zones)
                    set.add(z);
        }
    }
    catch {
        // Older engine — the seeded common set stands in as the fallback.
    }
    return Array.from(set).sort();
}
/**
 * Canonical IANA zone identifiers the picker offers and the API accepts.
 * Sourced from the platform; computed once at module load.
 */
export const SUPPORTED_TIME_ZONES = computeSupportedZones();
const SUPPORTED_SET = new Set(SUPPORTED_TIME_ZONES);
/**
 * True iff `tz` is a valid IANA zone the platform can format with. The
 * server calls this to validate a merchant-supplied `display_time_zone`
 * BEFORE write (reject-with-400 on false); the picker uses the same set so
 * the two never diverge. Fast membership check first, then a live
 * `Intl.DateTimeFormat` probe as a fallback for engines whose supported-list
 * is a strict subset of what they can actually render.
 */
export function isValidTimeZone(tz) {
    if (typeof tz !== 'string' || tz.length === 0)
        return false;
    if (tz === DEFAULT_TIME_ZONE)
        return true;
    if (SUPPORTED_SET.has(tz))
        return true;
    try {
        // Throws RangeError for an unknown/invalid zone.
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}
// ---------------------------------------------------------------------------
// Calendar days in a display zone → UTC instants (visits date filter,
// filters cleanup 2026-10-15). Signed data stays UTC; only the boundaries
// of a merchant-chosen day move to the merchant's zone.
// ---------------------------------------------------------------------------
/** `utcMs` as wall-clock time in `tz`, minus `utcMs` (the zone offset, ms). */
function zoneOffsetMs(utcMs, tz) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(new Date(utcMs));
    const p = {};
    for (const x of parts)
        if (x.type !== 'literal')
            p[x.type] = Number(x.value);
    const wall = Date.UTC(p['year'], p['month'] - 1, p['day'], p['hour'] % 24, p['minute'], p['second']);
    return wall - Math.floor(utcMs / 1000) * 1000;
}
/** The UTC instant at which calendar day `day` (YYYY-MM-DD) starts in `tz`. */
export function zonedDayStartUtc(day, tz) {
    const [y, m, d] = day.split('-').map(Number);
    const wall = Date.UTC(y, m - 1, d);
    // Two passes settle the offset across a DST change.
    const first = wall - zoneOffsetMs(wall, tz);
    return new Date(wall - zoneOffsetMs(first, tz));
}
/** Inclusive bounds of the day range [from, to] in `tz`, as ISO UTC strings. */
export function zonedDayRangeUtc(from, to, tz = DEFAULT_TIME_ZONE) {
    const next = (day) => {
        const [y, m, d] = day.split('-').map(Number);
        return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
    };
    return {
        from_iso: from !== undefined ? zonedDayStartUtc(from, tz).toISOString() : null,
        to_iso: to !== undefined ? new Date(zonedDayStartUtc(next(to), tz).getTime() - 1).toISOString() : null,
    };
}
/** Today's calendar day (YYYY-MM-DD) in `tz`. */
export function todayInZone(tz, now = new Date()) {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}
//# sourceMappingURL=time-zone.js.map